/** Thin fetch wrapper around the badger-backend `lightspeed_integration` function.
 *
 * The whole synchronous API is ONE routed Cloud Function that dispatches on the last path
 * segment, so every call is `${backendBaseUrl}/${route}` (see backend `router.py`).
 */
import { config } from '../config/env'
import type {
  CustomerResponse,
  PendingRequest,
  PendingResponse,
  ValidateResponse,
} from './types'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Transport seam. We call our own backend with plain `fetch` — simplest and most debuggable.
// IF the POS web view turns out to block direct outbound requests (CORS / network policy),
// the only change needed is to swap the body of `request()` to route through the host's
// `pos_apiCall(path, method, data, mimeType, cb)` — TanStack Query and everything above stays
// untouched. Whether pos_apiCall can even target an external host (vs Lightspeed-relative
// paths) is unverified — confirm on the test iPad (DEV-67) before relying on it.
async function request<T>(route: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${config.backendBaseUrl}/${route}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })

  if (!res.ok) {
    // Backend is a 501 skeleton today; surface the status so the till UI can show it.
    const body = await res.text().catch(() => '')
    throw new ApiError(res.status, body || res.statusText)
  }

  return (await res.json()) as T
}

export const api = {
  /** POST /validate — resolve webext_uuid → business + location. Hot path. */
  validate(webextUuid: string): Promise<ValidateResponse> {
    return request<ValidateResponse>('validate', {
      method: 'POST',
      body: JSON.stringify({ webext_uuid: webextUuid }),
    })
  },

  /** GET /customer — scanned QR → consumer + loyalty state.
   * The backend omits `cards`/`available_rewards` when empty (and shapes are still PROPOSED),
   * so default them to arrays here — the screen reads `.length`/`.map` and must not crash. */
  async customer(webextUuid: string, qr: string): Promise<CustomerResponse> {
    const params = new URLSearchParams({ webext_uuid: webextUuid, qr })
    const res = await request<CustomerResponse>(`customer?${params.toString()}`, { method: 'GET' })
    return { ...res, cards: res.cards ?? [], available_rewards: res.available_rewards ?? [] }
  },

  /** POST /pending — record stamp/redemption intent (stamps commit server-side later). */
  pending(payload: PendingRequest): Promise<PendingResponse> {
    return request<PendingResponse>('pending', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
}

/** Pull a readable message out of a backend error body, which is JSON like
 * `{"ok": false, "error": "no consumer matches the scanned code"}`. Falls back to the raw text. */
function backendErrorText(body: string): string {
  try {
    const parsed = JSON.parse(body) as { error?: unknown }
    if (typeof parsed.error === 'string' && parsed.error.trim()) {
      const msg = parsed.error.trim()
      return msg.charAt(0).toUpperCase() + msg.slice(1) // "no consumer…" → "No consumer…"
    }
  } catch {
    /* not JSON — fall through to the raw body */
  }
  return body
}

/** Human-readable message for any thrown API/network error, for the cashier UI. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 501) return 'Backend not implemented yet (501).'
    return backendErrorText(error.message) || `Error ${error.status}`
  }
  return error instanceof Error ? error.message : 'Unexpected error'
}
